/**
 * Pazaryeri fiyatlaması: "bu ürünü Trendyol'da kaça listelemeliyim?"
 *
 * The whole formula lives HERE and nowhere else. It is deliberately not mirrored
 * in SQL: E31's FX bug survived a migration fix because the same ordering was
 * written twice (private.fx_direct and packages/core/src/fx.ts) and only one copy
 * was corrected. A price the screen shows and a price a report computes must come
 * from one function, so the database stores the *parameters* of a channel and this
 * module does the arithmetic.
 *
 * ── The three things that are easy to get wrong ───────────────────────────────
 *
 * 1. **KDV.** A VAT-registered seller never keeps the VAT it collects and never
 *    pays the VAT on the marketplace's invoices: the first is owed to the tax
 *    office, the second is deductible against it. So profit is a conversation
 *    between VAT-exclusive numbers only. Treating a %16 commission on a
 *    VAT-inclusive price as a %16 cost overstates it by a sixth.
 *
 * 2. **What the commission rate is a percentage *of*.** Trendyol publishes its
 *    category rates as "KDV dahil komisyon oranı" and charges them on the price
 *    the customer pays, so the deducted lira figure contains its own %20 VAT —
 *    which the seller deducts. Both halves of that sentence are parameters here
 *    (`commissionBase`, `commissionRateVatIncl`) because other marketplaces word
 *    it differently and Trendyol itself may change it; nothing is hard-coded.
 *
 * 3. **Stopaj is not a cost.** 9284 sayılı Cumhurbaşkanı Kararı (RG 22.12.2024)
 *    made the marketplace withhold %1 from the seller's payout from 1.1.2025, and
 *    330 Seri No.lu GVK Genel Tebliği m.5 sets the matrah as the VAT-EXCLUSIVE
 *    sale price — commission and shipping deductions do not reduce it. It is
 *    offset against the annual corporate tax (KVK 15), so it belongs in the cash
 *    block and must never be added to the cost stack. A seller who prices it in as
 *    a cost pays for it twice.
 *
 * `financeNet` (the cost of the money tied up until the payout lands) follows the
 * same convention as public.capital_cost: notional, simple interest, ACT/365, and
 * the screen says out loud that the tax office does not accept it as an expense.
 */
import { D, MoneyDecimal, type Dec, type DecimalInput } from '../money';

/** Is the commission a percentage of the VAT-inclusive price or the VAT-exclusive one? */
export type CommissionBase = 'gross' | 'net';

/** How the solved price is nudged up to a price a human would type. */
export type PriceRounding = 'none' | 'integer' | 'x90' | 'x99';

/** Which direction the calculation runs. */
export type PriceSolve = 'price' | 'margin';

/** Everything the channel decides. One row of public.sales_channels, as numbers. */
export interface ChannelParams {
  /** Komisyon oranı, % (Trendyol: kategori bazlı, satıcı panelinden). */
  commissionPct: DecimalInput;
  commissionBase: CommissionBase;
  /** The published rate already contains its own VAT (true for Trendyol). */
  commissionRateVatIncl: boolean;
  /** KDV on the channel's own invoices — komisyon, hizmet bedeli, kargo. Deductible. */
  feeVatPct: DecimalInput;
  /** Sipariş başına sabit hizmet/platform bedeli. */
  serviceFee: DecimalInput;
  serviceFeeVatIncl: boolean;
  /** Gönderi başına kargo — `shippingTariff` yoksa ya da desi ölçülmemişse kullanılır. */
  shippingFee: DecimalInput;
  shippingFeeVatIncl: boolean;
  /**
   * Taşıyıcının desi tarifesi: desi → gönderi bedeli.
   *
   * Kargo gönderi başınadır ve **desi paketle büyür** — 100 adet peçete 4 desi, 600
   * adet 24 desi. Sabit bir rakam tutmak paket merdivenini büyük paket lehine eğer,
   * ki merdivenin söylediği şey zaten "daha büyük paket sat": yanlış rakam önerdiği
   * kararı kayırır. Ambalajda düzeltilen hatanın kargodaki kardeşi buydu.
   *
   * Tarife kademeli ve DOĞRUSAL DEĞİL: DHL'de 4 desi 124,99 ₺ ama 24 desi 437,99 ₺,
   * yani altı katı hacim üç buçuk katı para. Bu yüzden oranla ölçeklenemez, tablodan
   * okunur. Desi tam sayıya YUKARI yuvarlanır — taşıyıcı öyle faturalıyor.
   */
  shippingTariff?: { vatIncl: boolean; rows: Record<string, number> };
  /** Reklam: ciro yüzdesi (KDV hariç hasılat üzerinden) ve/veya sipariş başına sabit. */
  adsPct: DecimalInput;
  adsPerOrder: DecimalInput;
  /** İade oranı, % of orders. */
  returnPct: DecimalInput;
  /** How many shipping legs the seller eats on a returned order (gidiş + dönüş = 2). */
  returnShipLegs: DecimalInput;
  /** Of a returned order's goods, the share that cannot be sold again, %. */
  returnScrapPct: DecimalInput;
  /** Satıştan hakedişe kaç gün (Trendyol: teslimat + vade). */
  payoutDays: DecimalInput;
  /** Yıllık para maliyeti, % — public.capital_cost_rates ile aynı anlam. */
  capitalAnnualPct: DecimalInput;
  /**
   * Komisyon hakedişten düşülerek mi tahsil ediliyor, yoksa ayrıca mı?
   *
   * Trendyol'un kendi hakediş sayfası "Komisyon faturaları, ödemelerden bağımsız
   * olarak tahsil edildiği için ekstrelere dahil edilmez" diyor. Doğruysa yatan para
   * komisyon kadar YÜKSEK ve Trendyol'a kendi zamanlaması olan ayrı bir borç var.
   * Kârı değiştirmez — komisyon her hâlükârda gider — yalnız nakdin ne zaman ve
   * hangi kapıdan çıktığını değiştirir, bu yüzden ayrı bir anahtar.
   */
  commissionInPayout?: boolean;
  /** E-ticaret tevkifatı, % (2026: 1). */
  withholdingPct: DecimalInput;
  /**
   * Beklenen kampanya indirimi, % — liste fiyatı ile fiilen satılan fiyat arasındaki fark.
   *
   * Pazaryeri satıcıyı kampanyaya sokuyorsa müşterinin ödediği liste fiyatı değildir ve
   * komisyon da dahil her şey o düşük fiyattan işler. Bu alan doluyken `gross` alıcının
   * ÖDEDİĞİ fiyat, `listGross` ise panele yazılacak fiyattır. Boş bırakılırsa ikisi aynı.
   */
  campaignDiscountPct?: DecimalInput;
  /** Kurumlar vergisi oranı, % — katkı payından net kâra köprü için; fiyatı etkilemez. */
  corporateTaxPct?: DecimalInput;
  rounding: PriceRounding;
}

/** Everything the listing decides: what goes in the box and what it cost us. */
export interface ListingParams {
  /** How many base units (adet) one listing sells. */
  packQty: DecimalInput;
  /** Birim maliyet, KDV hariç, baz birim başına (item_costs.avg_cost_base / standard_cost_base). */
  unitCostNet: DecimalInput;
  /** Ürünün KDV oranı, % (peçete: 20). */
  vatPct: DecimalInput;
  /** Koli, poşet, etiket, bant — KDV hariç, BİR ambalaj biriminin maliyeti. */
  packagingNet: DecimalInput;
  /**
   * Bir ambalaj biriminin kaç baz birim aldığı — Aslen'in kutusu 100 adet peçete.
   *
   * Without this the cost of a box is a flat per-listing scalar, which is right for
   * the listing it was measured on and WRONG for every other pack size: a 200'lü
   * listing needs two 100'lük boxes, a 600'lü needs six. The pack-size ladder exists
   * to compare exactly those, so a flat figure there quietly makes the bigger pack
   * look cheaper than it is — and the ladder's whole message is "go bigger".
   *
   * 0 or undefined means "not measured": the cost is then treated as flat and
   * `packagingUnits` comes back null so the screen can say so instead of implying a
   * box count it does not know.
   */
  packagingUnitQty?: DecimalInput;
  /** Paketleme işçiliği — KDV hariç, ilan başına. */
  labourNet: DecimalInput;
  /** Her şeyin dışında kalan (e-arşiv kontörü, entegratör aboneliği payı…), KDV hariç. */
  otherNet: DecimalInput;
  /**
   * Baz birim başına desi — 100 adetlik ilan 4 desi ise 0,04.
   *
   * Paket başına değil birim başına tutuluyor ki merdiven paket adedini değiştirdiğinde
   * desi kendiliğinden ölçeklensin. Ekran kullanıcıdan ilanın desisini alır ve
   * `desi / packQty` ile buraya çevirir.
   */
  desiPerUnit?: DecimalInput;
  /**
   * Genel gider payı — kira, ofis, mali müşavir, enerji; aylık toplamın bu ilana
   * düşen kısmı, KDV hariç.
   *
   * Satışın kendi maliyetleri (mal, kargo, komisyon) şirketi ayakta tutan giderleri
   * karşılamaz. Aslen'de bilinen aylık yük 83.564,65 ₺ ve bunun en büyük parçası
   * kira; ayda 3.000 ilan tabanında 27,85 ₺/ilan eder — ambalaj ile işçiliğin
   * toplamından fazla.
   *
   * Taban **normal kapasite** olmalı, fiili hacim değil: düşük hacimde fiili tabana
   * bölmek az satan ürünü daha da pahalı yapıp kısırdöngüyü besler. İşçilik zaten
   * `labourNet` içinde, buraya ikinci kez yazılmamalı.
   *
   * **Bu rakam stok değerlemesine GİRMEZ.** İki ayrı soru var ve karıştırılırsa
   * bilanço yanlış olur: *fiyatlama* "hangi fiyat bütün giderleri karşılar" diye sorar
   * ve kiranın cevaba girmesi zorunludur; *stok değerlemesi* "raftaki malın maliyeti
   * nedir" diye sorar ve TMS 2 p.16 depolama (b), stokun bugünkü hâline katkısı
   * olmayan genel yönetim (c) ve satış (d) giderlerini açıkça dışarıda bırakır. Aslen
   * bugün malı satın alıp sattığı için VUK 274 geçerli ve o da maliyet bedelini dar
   * tutuyor; VUK 275 ancak makineler çalışmaya başlayınca devreye girer ve genel idare
   * payını orada bile "ihtiyari" bırakır (275/4). Yani 31.12 envanterinde bu 27,85 ₺
   * aranmamalı — burası bir yönetim kararı ekranı, defter değil.
   */
  overheadNet: DecimalInput;
  /**
   * Mal satılana kadar stokta bekleyen gün sayısı.
   *
   * `payoutDays` yalnız pazaryerinin ödeme vadesini sayar, oysa para mal raftayken de
   * bağlı. Aslen'de 267.000 adet stok ve ayda 300.000 adetlik hedef hacim ≈ 27 gün.
   * Vade maliyeti ikisinin toplamı üzerinden işler.
   */
  inventoryDays?: DecimalInput;
}

export interface ChannelPriceInput extends ChannelParams, ListingParams {
  solve: PriceSolve;
  /** solve === 'price': hedef katkı marjı, KDV hariç hasılatın yüzdesi. */
  targetMarginPct?: DecimalInput;
  /** solve === 'margin': elde olan liste fiyatı, KDV dahil. */
  listPrice?: DecimalInput;
}

/** One line of the breakdown. `net` is what hits the P&L; `vat` is deductible. */
export interface CostLine {
  key: CostLineKey;
  net: Dec;
  vat: Dec;
  gross: Dec;
  /** True when the line grows with the price — the reason a price cannot be a simple sum. */
  scalesWithPrice: boolean;
  /** True when no money changes hands: notional, not a deductible expense. */
  notional: boolean;
}

export type CostLineKey =
  | 'goods'
  | 'packaging'
  | 'labour'
  | 'other'
  | 'overhead'
  | 'commission'
  | 'serviceFee'
  | 'shipping'
  | 'ads'
  | 'returns'
  | 'finance';

export interface ChannelPriceResult {
  /** Alıcının ödediği fiyat, KDV dahil — her şeyin hesaplandığı taban. */
  gross: Dec;
  /** Panele yazılacak liste fiyatı: `gross / (1 − kampanya indirimi)`. İndirim yoksa `gross`. */
  listGross: Dec;
  /** Hasılat, KDV hariç. */
  net: Dec;
  /** Hesaplanan KDV (satıştan). */
  vatOut: Dec;
  lines: CostLine[];
  /** Σ of the lines that do not move with the price. */
  fixedNet: Dec;
  /** Σ of the lines that do, as a fraction of net revenue (0…1). */
  rateOnNet: Dec;
  /** Toplam maliyet, KDV hariç. */
  totalCostNet: Dec;
  /** Katkı payı, KDV hariç. */
  profit: Dec;
  /** profit / net × 100 — null when there is no revenue to divide by. */
  marginPct: Dec | null;
  /** profit / totalCostNet × 100 — the "kaça alıp kaça satıyorum" view. */
  markupPct: Dec | null;
  /** The KDV dahil price at which profit is exactly zero. */
  breakEvenGross: Dec | null;
  /** %1 e-ticaret tevkifatı — mahsup edilir, gider DEĞİL. */
  withholding: Dec;
  /** Pazaryerinin hesaba yatırdığı tutar. */
  payout: Dec;
  /** İndirilecek KDV: komisyon + hizmet + kargo + malın alış KDV'si. */
  vatIn: Dec;
  /** Bu satışın tek başına doğurduğu KDV borcu (negatifse devreden KDV'yi besler). */
  vatPayable: Dec;
  /** payout − vatPayable: satıştan kalan gerçek nakit (stopaj hariç tutulmadan). */
  cashAfterVat: Dec;
  perPiece: { price: Dec; cost: Dec; profit: Dec };
  /** False when the target margin cannot be reached at any price (costs scale faster). */
  reachable: boolean;
  /** True when `rounding` pushed the solved price up. */
  rounded: boolean;
  /** Kaç ambalaj birimi gerekiyor; `packagingUnitQty` ölçülmemişse null. */
  packagingUnits: Dec | null;
  /** Kurumlar vergisi — katkı pozitifse; fiyat hesabına girmez. */
  corporateTax: Dec;
  /** Vergi sonrası kalan. */
  netProfit: Dec;
  /** Vade maliyetinin kaç gün üzerinden hesaplandığı (stok + ödeme vadesi). */
  financedDays: Dec;
  /** Bu ilanın faturalanacak desisi (yukarı yuvarlanmış); tarife kullanılmadıysa null. */
  desi: Dec | null;
  /** Kargo bedeli tarifeden mi geldi, elle girilen sabit rakamdan mı? */
  shippingSource: 'tariff' | 'flat';
}

const HUNDRED = D(100);

/** x / 100 as a Decimal fraction. */
function frac(pct: DecimalInput): Dec {
  return D(pct).dividedBy(HUNDRED);
}

/** Strip the VAT out of an amount that carries it, or leave a VAT-exclusive one alone. */
function netOf(amount: DecimalInput, vatIncluded: boolean, vatFrac: Dec): Dec {
  const a = D(amount);
  return vatIncluded ? a.dividedBy(D(1).plus(vatFrac)) : a;
}

/**
 * The commission as a fraction of NET revenue.
 *
 * A rate charged on the VAT-inclusive price is a bigger bite out of the
 * VAT-exclusive revenue, by exactly (1 + product VAT); a rate that carries its own
 * VAT is a smaller cost, by exactly (1 + fee VAT). With Trendyol's %20/%20 the two
 * cancel and a "%16 KDV dahil oran on the gross price" costs %16 of net revenue —
 * but they only cancel while the two rates are equal, so neither is assumed.
 */
function commissionRateOnNet(p: ChannelParams, productVatFrac: Dec, feeVatFrac: Dec): Dec {
  let k = frac(p.commissionPct);
  if (p.commissionBase === 'gross') k = k.times(D(1).plus(productVatFrac));
  if (p.commissionRateVatIncl) k = k.dividedBy(D(1).plus(feeVatFrac));
  return k;
}

/**
 * Ceil `value` to the next price ending in the chosen pattern.
 *
 * Always upward: a price rounded DOWN to look nicer silently undercuts the target
 * margin, which is the one thing this screen exists to protect.
 */
function roundUpPrice(value: Dec, mode: PriceRounding): Dec {
  const up = MoneyDecimal.ROUND_UP;
  if (mode === 'none') return value.toDecimalPlaces(2, up);
  if (mode === 'integer') return value.ceil();
  const whole = value.floor();
  const tail = mode === 'x90' ? D('0.90') : D('0.99');
  const candidate = whole.plus(tail);
  return (candidate.greaterThanOrEqualTo(value) ? candidate : whole.plus(1).plus(tail)).toDecimalPlaces(2, up);
}

/**
 * The whole card for one (listing, channel) pair.
 *
 * Solving for the price is one division, not a loop: every cost is either fixed or
 * a fixed fraction of net revenue, so with X = fixed costs, k = the price-scaling
 * fraction and m = the target margin,
 *
 *     N − kN − X = mN   ⟹   N = X / (1 − k − m)   ⟹   gross = N × (1 + KDV)
 *
 * When (1 − k − m) ≤ 0 the margin is unreachable at ANY price — the commission and
 * the ad budget already eat it — and `reachable` says so instead of the function
 * returning a negative or absurd price.
 */
export function computeChannelPrice(input: ChannelPriceInput): ChannelPriceResult {
  const v = frac(input.vatPct);
  const cv = frac(input.feeVatPct);
  const one = D(1);

  // ── costs that do not care what we charge ──────────────────────────────────
  const goodsNet = D(input.unitCostNet).times(D(input.packQty));
  /**
   * Boxes are bought whole. A 50'lik listing still consumes one 100'lük box — the
   * other half is waste, not a saving — so this is `ceil` and never a ratio.
   */
  const packUnitQty = D(input.packagingUnitQty ?? 0);
  const packagingUnits = packUnitQty.greaterThan(0) ? D(input.packQty).dividedBy(packUnitQty).ceil() : null;
  const packagingNet = D(input.packagingNet).times(packagingUnits ?? 1);
  const labourNet = D(input.labourNet);
  const otherNet = D(input.otherNet);
  const overheadNet = D(input.overheadNet);
  const serviceNet = netOf(input.serviceFee, input.serviceFeeVatIncl, cv);

  /**
   * Kargo: desi ölçülmüşse tarifeden, yoksa elle girilen sabit rakamdan.
   *
   * Aranan desi tabloda yoksa bir ÜSTTEKİ satır alınır, tablonun sonundan taşıyorsa
   * son satır. Aşağı yuvarlamak gönderiyi olduğundan ucuz gösterirdi; tablonun
   * ötesinde (30 desi üzeri) zaten taşıyıcıyla ayrıca mutabık kalmak gerekiyor, o
   * yüzden son satır bir alt sınırdır ve ekran bunu söylemek zorunda.
   */
  const desiPerUnit = D(input.desiPerUnit ?? 0);
  const tariff = input.shippingTariff;
  const desi = tariff && desiPerUnit.greaterThan(0) ? desiPerUnit.times(D(input.packQty)).ceil() : null;
  let shippingNet: Dec;
  let shippingSource: 'tariff' | 'flat';
  if (tariff && desi) {
    const keys = Object.keys(tariff.rows)
      .map(Number)
      .filter((k) => Number.isFinite(k))
      .sort((a, b) => a - b);
    const want = desi.toNumber();
    const row = keys.find((k) => k >= want) ?? keys[keys.length - 1];
    shippingNet = netOf(tariff.rows[String(row)] ?? 0, tariff.vatIncl, cv);
    shippingSource = 'tariff';
  } else {
    shippingNet = netOf(input.shippingFee, input.shippingFeeVatIncl, cv);
    shippingSource = 'flat';
  }
  const adsFixedNet = D(input.adsPerOrder);

  /**
   * İade yükü, per order that is KEPT.
   *
   * A %5 return rate means one return for every 19 kept orders, so the burden is
   * r/(1−r) and not r: the returns have to be carried by the sales that survive.
   * What a return actually costs — the shipping legs the seller pays (Trendyol
   * invoices the return leg at its own agreement rate), the dispatch service fee
   * that is not refunded, the packaging and the labour that are gone, and the share
   * of the goods that cannot be sold again. The commission is NOT in there: a
   * returned order's commission is offset against the next hakediş.
   *
   * The scrapped goods are charged at their VAT-INCLUSIVE cost on purpose. KDVK
   * 30/c denies the deduction of input VAT on zayi olan mal, so for the portion
   * that comes back unsaleable the %20 already deducted has to be given back — the
   * VAT stops being recoverable and becomes part of the loss.
   */
  const r = frac(input.returnPct);
  const kept = one.minus(r);
  const burden = kept.greaterThan(0) ? r.dividedBy(kept) : D(0);
  const scrapNet = goodsNet.times(frac(input.returnScrapPct));
  const returnShipNet = shippingNet.times(D(input.returnShipLegs));
  const returnsNet = burden.times(
    returnShipNet.plus(serviceNet).plus(packagingNet).plus(labourNet).plus(scrapNet.times(one.plus(v))),
  );
  /** Only the carrier and service legs of a return carry deductible VAT. */
  const returnsVat = burden.times(returnShipNet.plus(serviceNet).times(cv).plus(packagingNet.times(v)));

  /**
   * Vade maliyeti. The goods, the box and the hands are paid for before the money
   * comes back, so that outlay is out of the business for the whole window — the days
   * the goods sit in stock PLUS the days the marketplace holds the payout. Counting
   * only the payout term, as the first version did, understated it by about two
   * thirds: 27 days of stock against 14 days of term.
   *
   * Notional, like public.capital_cost: simple interest, ACT/365, no compounding.
   * The channel's own deductions are not financed — they never reach our account, and
   * neither is the overhead, which is paid monthly as it falls due rather than
   * pre-funded per listing.
   */
  const financedDays = D(input.inventoryDays ?? 0).plus(D(input.payoutDays));
  const financeNet = goodsNet
    .plus(packagingNet)
    .plus(labourNet)
    .plus(otherNet)
    .times(frac(input.capitalAnnualPct))
    .times(financedDays)
    .dividedBy(365);

  const fixedNet = goodsNet
    .plus(packagingNet)
    .plus(labourNet)
    .plus(otherNet)
    .plus(overheadNet)
    .plus(serviceNet)
    .plus(shippingNet)
    .plus(adsFixedNet)
    .plus(returnsNet)
    .plus(financeNet);

  // ── costs that are a slice of whatever we charge ───────────────────────────
  const kCommission = commissionRateOnNet(input, v, cv);
  const kAds = frac(input.adsPct);
  const rateOnNet = kCommission.plus(kAds);

  // ── the price ──────────────────────────────────────────────────────────────
  const m = frac(input.targetMarginPct ?? 0);
  const denom = one.minus(rateOnNet).minus(m);
  const reachable = input.solve === 'margin' ? true : denom.greaterThan(0);

  /**
   * Kampanya indirimi varsa iki fiyat vardır ve karıştırılırsa model yanlış taban
   * üzerine kurulur: `listGross` panele yazılan, `gross` alıcının ÖDEDİĞİ. Komisyon da
   * kargo da indirimli fiyattan işler, yani her hesap `gross` üzerinden yürür.
   * Yuvarlama listeye uygulanır — panele yazılan rakam odur — ve ödenen fiyat ondan
   * geri hesaplanır, böylece ekranda görünen marj gerçekten tahsil edilecek marjdır.
   */
  const keep = one.minus(frac(input.campaignDiscountPct ?? 0));
  let gross: Dec;
  let listGross: Dec;
  let rounded = false;
  if (input.solve === 'margin') {
    listGross = D(input.listPrice);
    gross = listGross.times(keep);
  } else if (!reachable) {
    gross = D(0);
    listGross = D(0);
  } else {
    const raw = fixedNet.dividedBy(denom).times(one.plus(v));
    const rawList = keep.greaterThan(0) ? raw.dividedBy(keep) : raw;
    listGross = roundUpPrice(rawList, input.rounding);
    rounded = listGross.greaterThan(rawList.toDecimalPlaces(2, MoneyDecimal.ROUND_UP));
    gross = listGross.times(keep);
  }

  const net = gross.dividedBy(one.plus(v));
  const vatOut = gross.minus(net);

  // ── the breakdown, now that the price is known ─────────────────────────────
  const commissionNet = net.times(kCommission);
  const adsPctNet = net.times(kAds);
  /** `vat` is the DEDUCTIBLE VAT the line carries, in lira — not a rate, because the
   *  returns line bundles four different rates and a non-deductible part. */
  const line = (key: CostLineKey, netAmount: Dec, vatAmount: Dec, scales: boolean, notional = false): CostLine => ({
    key,
    net: netAmount,
    vat: vatAmount,
    gross: netAmount.plus(vatAmount),
    scalesWithPrice: scales,
    notional,
  });

  const lines: CostLine[] = [
    line('goods', goodsNet, goodsNet.times(v), false),
    line('packaging', packagingNet, packagingNet.times(v), false),
    line('labour', labourNet, D(0), false),
    line('other', otherNet, otherNet.times(v), false),
    // Genel giderin KDV'si burada hesaplanmıyor: havuz kira, sigorta, mali müşavir gibi
    // farklı oranlardan (ve kısmen KDV'siz) kalemler karışımı, tek bir oranla çarpmak
    // uydurma olurdu. Fiyat KDV hariç tutarla kurulduğu için sonuç etkilenmiyor.
    line('overhead', overheadNet, D(0), false),
    line('commission', commissionNet, commissionNet.times(cv), true),
    line('serviceFee', serviceNet, serviceNet.times(cv), false),
    line('shipping', shippingNet, shippingNet.times(cv), false),
    line('ads', adsPctNet.plus(adsFixedNet), adsPctNet.plus(adsFixedNet).times(cv), !kAds.isZero()),
    line('returns', returnsNet, returnsVat, false),
    line('finance', financeNet, D(0), false, true),
  ].filter((l) => !l.net.isZero());

  const totalCostNet = fixedNet.plus(commissionNet).plus(adsPctNet);
  const profit = net.minus(totalCostNet);
  const marginPct = net.isZero() ? null : profit.dividedBy(net).times(HUNDRED);
  const markupPct = totalCostNet.isZero() ? null : profit.dividedBy(totalCostNet).times(HUNDRED);
  const breakEven = one.minus(rateOnNet);
  const breakEvenGross = breakEven.greaterThan(0) ? fixedNet.dividedBy(breakEven).times(one.plus(v)) : null;

  /**
   * Tevkifat matrahı = KDV hariç satış bedeli (330 Seri No.lu GVK GT m.5).
   * NOT the gross, NOT the payout: the commission and the shipping the marketplace
   * deducts do not shrink it. It is deliberately absent from `totalCostNet`.
   */
  const withholding = net.times(frac(input.withholdingPct));

  // what the marketplace actually deposits: price minus the invoices it nets off and
  // the stopaj. A commission collected separately raises the payout without touching
  // the profit — the money still leaves, just through another door and on another day.
  const commissionDeducted = input.commissionInPayout === false ? D(0) : commissionNet.times(one.plus(cv));
  const channelDeductions = commissionDeducted
    .plus(serviceNet.times(one.plus(cv)))
    .plus(shippingNet.times(one.plus(cv)));
  const payout = gross.minus(channelDeductions).minus(withholding);

  // indirilecek KDV: the channel's invoices plus the VAT on buying the goods
  const vatIn = lines.filter((l) => !l.notional).reduce((acc, l) => acc.plus(l.vat), D(0));
  const vatPayable = vatOut.minus(vatIn);

  /** Kurumlar vergisi: zarar eden bir satış vergi doğurmaz, mahsup dönem içinde işler. */
  const corporateTax = profit.greaterThan(0) ? profit.times(frac(input.corporateTaxPct ?? 0)) : D(0);

  const qty = D(input.packQty);
  const perPiece = qty.isZero()
    ? { price: D(0), cost: D(0), profit: D(0) }
    : { price: gross.dividedBy(qty), cost: totalCostNet.dividedBy(qty), profit: profit.dividedBy(qty) };

  return {
    gross,
    listGross,
    net,
    vatOut,
    lines,
    fixedNet,
    rateOnNet,
    totalCostNet,
    profit,
    marginPct,
    markupPct,
    breakEvenGross,
    withholding,
    payout,
    vatIn,
    vatPayable,
    cashAfterVat: payout.minus(vatPayable),
    perPiece,
    reachable,
    rounded,
    packagingUnits,
    corporateTax,
    netProfit: profit.minus(corporateTax),
    financedDays,
    desi,
    shippingSource,
  };
}
